// MUI theme — the single source of truth for the app's design language.
// All decorative styling (color, type, radius, component defaults) lives here;
// components co-locate only layout via `sx`. The app ships light and dark
// variants of the Sanborn navy / royal-blue palette; `buildTheme(mode)`
// returns the theme for a mode and ThemeModeProvider (theme-mode.jsx) picks
// the mode from the user's toggle / system preference.
import { createTheme, alpha } from '@mui/material/styles';

// Per-mode design tokens. The palette follows the Sanborn mockup's navy /
// royal-blue brand (mockups/initial_mockup.png) with a modern twist: light
// mode is the mockup's airy white-on-blue-gray look, dark mode is a
// sophisticated navy (not generic gray) with a lighter blue accent for
// legibility, and a sparing amber secondary for badges/callouts.
const TOKENS = {
  dark: {
    bg: '#07162e', // app background — deepest navy, lets surfaces lift
    surface: '#0d2347', // resting paper / cards — clearly above the bg
    surfaceRaised: '#14315f', // hover / raised surfaces
    border: 'rgba(148, 163, 184, 0.18)', // visible hairline on dark surfaces
    neutral: '#9db4d4', // base for hover tints / scrollbars / input fills
    text: '#e6edf7',
    textDim: '#9fb0c7',
    primary: {
      main: '#60a5fa', light: '#93c5fd', dark: '#2563eb', contrastText: '#06152b',
    },
    secondary: {
      main: '#fbbf24', light: '#fde68a', dark: '#d97706', contrastText: '#06152b',
    },
    // Soft shadow that lifts surfaces off the dark background without looking
    // glossy. Reused by cards and the product rows so elevation is consistent.
    cardShadow: '0 1px 2px rgba(0,8,24,0.35), 0 18px 44px -18px rgba(0,8,24,0.42)',
    // Map palette: navy land with a crisp lighter-blue outline (county
    // separation meets the 3:1 non-text contrast bar). The selection is the
    // amber secondary rather than blue-on-blue: the hue shift plus ~6.8:1
    // contrast against the land keeps even small city geometries legible.
    map: {
      land: '#243a5e', landStroke: '#8eb1dc', highlight: '#fbbf24', highlightStroke: '#fde68a', backdrop: '#0a1a35', tileGrid: '#38bdf8',
    },
    // County participation choropleth ramp. Two greens (committed/active) plus
    // a neutral grey for the un-engaged default. Tuned to read on the dark
    // surface and to stay separable in greyscale (lightness steps, not hue
    // alone) so the status is never conveyed by color alone.
    participation: {
      active_quote: '#1b7a3d', committed: '#52b788', interest_not_expressed: '#5a6470',
    },
  },
  light: {
    bg: '#f3f7fb', // app background — airy blue-tinted near-white
    surface: '#ffffff', // resting paper / cards
    surfaceRaised: '#eef4fb', // hover / raised surfaces
    border: 'rgba(13, 35, 71, 0.12)', // visible hairline on light surfaces
    neutral: '#52657a',
    text: '#102033',
    textDim: '#52657a',
    // The mockup's royal-blue primary, deep navy on the dark end so buttons
    // and links keep WCAG contrast against white surfaces.
    primary: {
      main: '#1d4ed8', light: '#2563eb', dark: '#0b3d91', contrastText: '#ffffff',
    },
    secondary: {
      main: '#b45309', light: '#f59e0b', dark: '#7c2d12', contrastText: '#ffffff',
    },
    cardShadow: '0 1px 2px rgba(13,35,71,0.05), 0 12px 30px -12px rgba(13,35,71,0.18)',
    map: {
      land: '#dce3ec', landStroke: '#64748b', highlight: '#2563eb', highlightStroke: '#1d4ed8', backdrop: '#f8fbff', tileGrid: '#c2410c',
    },
    participation: {
      active_quote: '#1b6e3b', committed: '#74c69d', interest_not_expressed: '#c2c8d0',
    },
  },
};

// The admin-editable subset of each mode's tokens, flattened for the branding
// editor and the `theme_palette` portal setting. Maps onto TOKENS in
// buildTheme; keys absent from an override fall back to the built-ins below.
export const DEFAULT_THEME_PALETTE = Object.freeze({
  light: Object.freeze({
    bg: TOKENS.light.bg,
    surface: TOKENS.light.surface,
    surfaceRaised: TOKENS.light.surfaceRaised,
    text: TOKENS.light.text,
    textDim: TOKENS.light.textDim,
    primary: TOKENS.light.primary.main,
    secondary: TOKENS.light.secondary.main,
    mapLand: TOKENS.light.map.land,
    mapLandStroke: TOKENS.light.map.landStroke,
    mapHighlight: TOKENS.light.map.highlight,
    mapTopoGrid: TOKENS.light.map.tileGrid,
    mapBackdrop: TOKENS.light.map.backdrop,
  }),
  dark: Object.freeze({
    bg: TOKENS.dark.bg,
    surface: TOKENS.dark.surface,
    surfaceRaised: TOKENS.dark.surfaceRaised,
    text: TOKENS.dark.text,
    textDim: TOKENS.dark.textDim,
    primary: TOKENS.dark.primary.main,
    secondary: TOKENS.dark.secondary.main,
    mapLand: TOKENS.dark.map.land,
    mapLandStroke: TOKENS.dark.map.landStroke,
    mapHighlight: TOKENS.dark.map.highlight,
    mapTopoGrid: TOKENS.dark.map.tileGrid,
    mapBackdrop: TOKENS.dark.map.backdrop,
  }),
});

// Layout constants referenced from component `sx`, kept here so the few fixed
// dimensions in the app have one home rather than being scattered as literals.
export const layout = {
  maxContentWidth: 1400,
  headerHeight: 64,
  areaColumnWidth: 340,
  summaryColumnWidth: 280,
};

// Shared style for Material Symbols ligature icons (DB-driven product icon_key
// values and the drawer chevron). The font is loaded in index.html.
export const materialSymbolSx = {
  fontFamily: '"Material Symbols Outlined"',
  fontWeight: 'normal',
  fontStyle: 'normal',
  lineHeight: 1,
  letterSpacing: 'normal',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  wordWrap: 'normal',
  direction: 'ltr',
  fontFeatureSettings: '"liga"',
  WebkitFontSmoothing: 'antialiased',
};

// Builds the MUI theme for a mode. `paletteOverrides` is the admin-edited
// `theme_palette[mode]` object (flat keys, see DEFAULT_THEME_PALETTE); any
// missing/invalid keys fall back to the built-in tokens. When primary or
// secondary are overridden only `main` is supplied so MUI derives the
// light/dark/contrastText variants with its contrast-aware augmentation.
export const buildTheme = (mode, paletteOverrides = null) => {
  const base = TOKENS[mode] ?? TOKENS.dark;
  const o = paletteOverrides ?? {};
  const t = {
    ...base,
    bg: o.bg || base.bg,
    surface: o.surface || base.surface,
    surfaceRaised: o.surfaceRaised || base.surfaceRaised,
    text: o.text || base.text,
    textDim: o.textDim || base.textDim,
    primary: o.primary ? { main: o.primary } : base.primary,
    secondary: o.secondary ? { main: o.secondary } : base.secondary,
    map: {
      land: o.mapLand || base.map.land,
      landStroke: o.mapLandStroke || base.map.landStroke,
      highlight: o.mapHighlight || base.map.highlight,
      // The thin outline that fattens small highlights tracks the highlight
      // color when overridden so they never clash.
      highlightStroke: o.mapHighlight || base.map.highlightStroke,
      backdrop: o.mapBackdrop || base.map.backdrop,
      // Tile-grid stroke is now an editable token (mapTopoGrid); it falls back
      // to the palette-complementary built-in chosen to contrast both the land
      // and the highlight fill.
      tileGrid: o.mapTopoGrid || base.map.tileGrid,
    },
  };
  return createTheme({
    palette: {
      mode,
      primary: t.primary,
      secondary: t.secondary,
      text: { primary: t.text, secondary: t.textDim },
      divider: t.border,
      background: { default: t.bg, paper: t.surface },
      action: {
        hover: alpha(t.neutral, 0.08),
        selected: alpha(t.primary.main, 0.14),
        focus: alpha(t.primary.main, 0.24),
      },
    },
    // Map palette, carried on the theme so the location SVG shares the active
    // mode's surface ramp (read via useTheme().mapColors).
    mapColors: t.map,
    // Participation choropleth ramp (not admin-editable), read via
    // useTheme().participationColors by the public map + status chips.
    participationColors: base.participation,
    shape: { borderRadius: 10 },
    typography: {
      fontFamily: '"Inter", "Helvetica Neue", Arial, sans-serif',
      h1: { fontSize: '2rem', fontWeight: 700, letterSpacing: '-0.02em' },
      h2: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.01em' },
      h5: { fontWeight: 700, letterSpacing: '-0.01em' },
      // Compact uppercase section heading shared by the estimate panel and the
      // product grid; styling lives in the theme so call sites stay declarative.
      overline: { fontWeight: 700, letterSpacing: '0.08em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          'html, body, #root': { margin: 0, padding: 0, height: '100%' },
          body: { backgroundColor: t.bg },
          // MUI tints dark Paper with a translucent overlay gradient; opt out so
          // surfaces stay the flat, intentional slate set in the palette.
          '*::-webkit-scrollbar': { width: 10, height: 10 },
          '*::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(t.neutral, 0.25),
            borderRadius: 8,
            border: `2px solid ${t.bg}`,
          },
          '*::-webkit-scrollbar-thumb:hover': { backgroundColor: alpha(t.neutral, 0.4) },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: t.border },
        },
      },
      MuiCard: {
        defaultProps: { variant: 'outlined' },
        styleOverrides: {
          root: {
            backgroundColor: t.surface,
            borderColor: t.border,
            boxShadow: t.cardShadow,
          },
        },
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { textTransform: 'none', fontWeight: 600, borderRadius: 10 },
          sizeLarge: { paddingTop: 12, paddingBottom: 12, fontSize: '1rem' },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            backgroundColor: alpha(t.neutral, 0.06),
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: alpha(t.neutral, 0.32) },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.primary.main, borderWidth: 1.5 },
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: { backgroundColor: t.surfaceRaised, border: `1px solid ${t.border}`, fontSize: '0.75rem' },
          arrow: { color: t.surfaceRaised },
        },
      },
    },
  });
};
